import { getThemeToProvide, getThemeToProvideSync, isSyncThemeSettings, trackTheme } from '../components/ThemeProvider/ThemeProvider.helpers';
import { DEFAULT_SETTINGS, getChannelFromHtml, getLocalThemeSettings, setTrellisEnabledFlag, validateThemeVersion } from './getLocalThemeSettings';
import devLogger from 'react-utils/devLogger';
export const TRELLIS_THEME_EVENT = 'trellis:theme';
export const TRELLIS_THEME_REQUEST_EVENT = 'trellis:theme:request';
export class ThemeManager {
  // Set when a parent app responds to the cross-origin handshake. Persisted so
  // subsequent setTheme calls (e.g. from ThemeProvider re-renders) don't reset
  // the channel back to the default.
  constructor() {
    this.themeRequestCounter = 0;
    this.subscribers = new Set();
    this.setThemeObject = theme => {
      this.theme = theme;
      this.subscribers.forEach(callback => callback());
    };
    this.subscribeToWindowEvents = () => {
      window.addEventListener('message', this.handleUpdateEvent);
    };
    this.requestParentChannel = () => {
      if (window.parent !== window) {
        window.parent.postMessage({
          type: TRELLIS_THEME_REQUEST_EVENT
        }, '*');
      }
    };
    this.broadcastToChildFrames = () => {
      const channel = getChannelFromHtml();
      const themeVersion = {
        type: 'deployed',
        channel
      };
      Array.from(document.querySelectorAll('iframe')).forEach(iframe => {
        var _iframe$contentWindow;
        (_iframe$contentWindow = iframe.contentWindow) === null || _iframe$contentWindow === void 0 || _iframe$contentWindow.postMessage({
          type: TRELLIS_THEME_EVENT,
          themeVersion
        }, '*');
      });
    };
    this.destroy = () => {
      window.removeEventListener('message', this.handleUpdateEvent);
    };
    this.messageHandlers = {
      [TRELLIS_THEME_REQUEST_EVENT]: event => {
        const channel = getChannelFromHtml();
        if (event.source && event.source !== window) {
          const themeVersion = {
            type: 'deployed',
            channel
          };
          event.source.postMessage({
            type: TRELLIS_THEME_EVENT,
            themeVersion
          }, '*');
        }
      },
      [TRELLIS_THEME_EVENT]: event => {
        const newThemeSettings = getLocalThemeSettings();
        if (validateThemeVersion(event.data.themeVersion)) {
          this.propagatedThemeVersion = event.data.themeVersion;
          newThemeSettings.themeVersion = event.data.themeVersion;
        }
        this.handleUpdate(newThemeSettings);
      }
    };
    this.handleUpdateEvent = event => {
      var _event$data;
      const type = event === null || event === void 0 || (_event$data = event.data) === null || _event$data === void 0 ? void 0 : _event$data.type;
      if ([TRELLIS_THEME_EVENT, TRELLIS_THEME_REQUEST_EVENT].includes(type)) {
        var _this$messageHandlers, _this$messageHandlers2;
        (_this$messageHandlers = (_this$messageHandlers2 = this.messageHandlers)[type]) === null || _this$messageHandlers === void 0 || _this$messageHandlers.call(_this$messageHandlers2, event);
      }
    };
    this.handleUpdate = newThemeSettings => {
      trackTheme(newThemeSettings.themeName, newThemeSettings.themeMode);
      this.themeRequestCounter += 1;
      const currentRequestId = this.themeRequestCounter;
      if (isSyncThemeSettings(newThemeSettings)) {
        const syncTheme = getThemeToProvideSync(newThemeSettings);
        this.setThemeObject(syncTheme);
      } else {
        getThemeToProvide(newThemeSettings).then(theme => {
          if (currentRequestId === this.themeRequestCounter) {
            this.setThemeObject(theme);
          }
        }).catch(error => {
          devLogger.warn({
            message: `Failed to load theme with error: ${error}`,
            key: 'foundations-theming:theme-manager:theme-load-failure'
          });
        });
      }
    };
    this.subscribe = callback => {
      this.subscribers.add(callback);
      return () => {
        this.subscribers.delete(callback);
      };
    };
    this.getTheme = () => {
      return this.theme;
    };
    this.setTheme = themeSettings => {
      const settingsOverrides = Object.fromEntries(Object.entries(themeSettings).filter(([, v]) => v !== undefined));
      const newThemeSettings = Object.assign({}, DEFAULT_SETTINGS, this.propagatedThemeVersion !== undefined && {
        themeVersion: this.propagatedThemeVersion
      }, settingsOverrides);
      this.handleUpdate(newThemeSettings);
    };
    const _themeSettings = getLocalThemeSettings();
    this.theme = getThemeToProvideSync(_themeSettings);
    this.handleUpdate(_themeSettings);
  }
  /**
   * Allow the theme manager to read the `trellis:theme` setting from localStorage, and update the theme object.
   */
  setTrellisEnabled(value) {
    setTrellisEnabledFlag(value);
    const themeSettings = getLocalThemeSettings();
    this.handleUpdate(themeSettings);
  }

  /**
   * If running inside an iframe, asks the parent app's ThemeManager for the
   * current theme channel. The parent responds with a `trellis:theme` message
   * containing `themeVersion`, which `handleUpdateEvent` applies automatically.
   *
   * Called automatically by `subscribeToWindowEvents` on init. Can also be
   * called directly if the channel needs to be re-requested (e.g. after HMR).
   */
}
export let globalThemeManager;
export function createGlobalThemeManager() {
  if (globalThemeManager) {
    globalThemeManager.destroy();
  }
  globalThemeManager = new ThemeManager();
  globalThemeManager.subscribeToWindowEvents();
  globalThemeManager.requestParentChannel();
}
createGlobalThemeManager();